import React, { useEffect, useMemo, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { auth, db } from "../App";
import PlatformModeSwitch from "../components/PlatformModeSwitch";
import { usePlatformMode } from "../context/PlatformModeContext";
import { doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { portalShellSublabelFromProfile } from "../clientPortal/displayUtils";

function classNames(...xs) {
  return xs.filter(Boolean).join(" ");
}

function uniqueStrings(values) {
  return Array.from(new Set((Array.isArray(values) ? values : []).map((value) => (value || "").toString().trim().toLowerCase()).filter(Boolean)));
}

function getEffectiveRoles(profile) {
  return uniqueStrings([
    profile?.role,
    profile?.primaryUserType,
    ...(Array.isArray(profile?.allowedUserTypes) ? profile.allowedUserTypes : []),
    ...(Array.isArray(profile?.constructionRoles) ? profile.constructionRoles : []),
  ]);
}

function roleOk(profile) {
  const roles = getEffectiveRoles(profile);
  return roles.some((role) => ["trade", "subtrade", "contractor", "designer", "engineer", "supplier", "consultant", "reviewer", "client", "admin"].includes(role));
}

function roleLabel(profile) {
  const roles = getEffectiveRoles(profile);
  const r = (profile?.primaryUserType || roles[0] || "trade").toString().toLowerCase();
  if (r === "designer") return "Designer";
  if (r === "engineer") return "Engineer";
  if (r === "supplier") return "Supplier";
  if (r === "consultant") return "Consultant";
  if (r === "reviewer") return "Reviewer";
  if (r === "contractor") return "Contractor";
  if (r === "subtrade") return "Subtrade";
  if (r === "admin") return "Admin";
  return "Trade";
}

async function ensureUserProfile(u) {
  const ref = doc(db, "users", u.uid);
  const snap = await getDoc(ref);

  if (!snap.exists()) {
    const email = u.email || "";
    await setDoc(ref, {
      email,
      role: "client",
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return { email, role: "client" };
  }

  return snap.data();
}

function TradeLayout() {
  const navigate = useNavigate();
  const { mode, setMode } = usePlatformMode();
  const [loading, setLoading] = useState(true);
  const [viewer, setViewer] = useState(null);
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState("");

  const ok = useMemo(() => roleOk(profile), [profile]);

  const portalBrandSublabel = useMemo(
    () => portalShellSublabelFromProfile(profile, { isAnonymous: !!viewer?.isAnonymous }),
    [profile, viewer?.isAnonymous]
  );

  // Force construction mode on Trade portal (real estate flows live elsewhere).
  useEffect(() => {
    if (mode !== "construction") setMode("construction");
  }, [mode, setMode]);

  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (u) => {
      setLoading(true);
      setError("");
      setViewer(null);
      setProfile(null);

      if (!u) {
        navigate("/client-portal");
        return;
      }

      setViewer(u);

      try {
        const prof = await ensureUserProfile(u);
        setProfile(prof);
        setLoading(false);
      } catch (e) {
        console.error("TradeLayout load error:", e);
        setError(e?.message || "Failed to load profile.");
        setLoading(false);
      }
    });

    return () => unsub();
  }, [navigate]);



  if (loading) return <div className="container mx-auto p-4 text-center">Loading portal…</div>;

  if (error || !viewer || !ok) {
    return (
      <div className="container mx-auto p-4">
        <div className="border rounded p-4 bg-white">
          <div className="text-red-600 mb-2">{error || "Access denied."}</div>
          <div className="text-sm opacity-70 mb-3">
            Required role: <span className="font-semibold">client/designer/engineer/supplier/contractor/consultant/reviewer</span> (admin allowed for testing).
          </div>
          <div className="flex items-center gap-2 flex-wrap">
          <PlatformModeSwitch size="sm" />
            <button className="border px-3 py-2 rounded" onClick={() => navigate("/")}>
              Home
            </button>
            <button className="border px-3 py-2 rounded" onClick={() => navigate("/admin/dashboard")}>
              Admin
            </button>
          </div>
        </div>
      </div>
    );
  }

  const nav = [{ to: "/client-portal", label: "Portal Home" }];

  return (
    <div className="container mx-auto p-4">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <Link to="/" className="no-underline text-slate-900" title="Portal home">
            <div className="text-xl font-bold leading-tight">Mark Beattie</div>
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{portalBrandSublabel}</div>
          </Link>
          <span className="text-sm opacity-70">{viewer.email}</span>
          <span className="px-2.5 py-1 rounded-full border bg-secondary text-xs">{roleLabel(profile)}</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <PlatformModeSwitch size="sm" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-4">
        <aside className="border rounded p-3">
          <div className="text-sm font-semibold mb-2 opacity-80">Portal</div>
          <nav className="flex flex-col gap-1">
            {nav.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end
                className={({ isActive }) =>
                  classNames("px-3 py-2 rounded", isActive ? "bg-accent text-white" : "hover:bg-secondary")
                }
              >
                {it.label}
              </NavLink>
            ))}
          </nav>
          <div className="mt-4 border-t pt-3 text-sm opacity-80">
            Assigned projects, scoped file access, comments, and revision uploads when enabled.
          </div>
        </aside>

        <section className="border rounded p-4 bg-white">
          <Outlet />
        </section>
      </div>
    </div>
  );
}

export default TradeLayout;
